import React, { useState, useEffect } from "react";

import api from "../../services/api";

import { UserLevelDisplay } from "../../models/enums/UserLevelDisplay";
import { User } from "../../models/entities/user";
import { Company } from "../../models/entities/company";

import PageContainer from "../../containers/PageContainer";
import TwygoUsers from "../../containers/TwygoUsers";
import TwygoCourses from "../../components/TwygoCourses";
import TwygoMassiveCourses from "../../components/TwygoMassiveCourses";
import TwygoLogsModal from "../../components/TwygoLogsModal";
import TwygoInfoModal from "../../components/TwygoInfoModal";

import { message, Checkbox, Tooltip } from "antd";
import {
  Container,
  CardContainer,
  BoxSearch,
  BoxActions,
  Actions,
  ContainerSearch,
  ContainerActions,
  Input,
  SearchIcon,
  Select,
  Button,
  UsersSelected,
  InfoIcon,
  LabelCheckbox,
  ActionButtonsContainer,
  HelpTooltip,
  HelpContainer,
} from "./styles";

const Twygo: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [companyUsers, setCompanyUsers] = useState<
    { user_id: number; full_name: string }[]
  >([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [actionType, setActionType] = useState<
    "send" | "inactivate" | "activate" | null
  >(null);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | undefined>(
    undefined
  );

  const [progressPercent, setProgressPercent] = useState(0);
  const [logs, setLogs] = useState<
    Array<{ message: string; type: "success" | "error" | "info" }>
  >([]);
  const [isBulkCoursesModalVisible, setIsBulkCoursesModalVisible] =
    useState(false);
  const [isLogsModalVisible, setIsLogsModalVisible] = useState(false);
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [logsModalTitle, setLogsModalTitle] = useState("Logs de Operação");

  const [filterName, setFilterName] = useState<string>("");
  const [profileFilter, setProfileFilter] = useState<number | undefined>(
    undefined
  );
  const [levelFilter, setLevelFilter] = useState<number | undefined>(undefined);
  const [profiles, setProfiles] = useState<{ id: number; name: string }[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [showHelpIconPulse, setShowHelpIconPulse] = useState(false);
  const [showInitialTooltip, setShowInitialTooltip] = useState(false);

  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [loadingMagicLink, setLoadingMagicLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingActions, setLoadingActions] = useState<{
    [key: string]: boolean;
  }>({});
  const [loadingBulkCourses, setLoadingBulkCourses] = useState(false);
  const [loadingCompanyFilter, setLoadingCompanyFilter] = useState(false);
  const [loadingCompanyUsers, setLoadingCompanyUsers] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchProfiles();

    const hasSeenHelpTwygo = localStorage.getItem("hasSeenHelpTwygo");
    if (!hasSeenHelpTwygo) {
      setShowHelpIconPulse(true);

      setTimeout(() => {
        setShowInitialTooltip(true);
      }, 1000);
    }
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyUsers(selectedCompany);
    } else {
      setCompanyUsers([]);
    }
  }, [selectedCompany]);

  const setActionLoading = (
    userId: number,
    action: string,
    isLoading: boolean
  ) => {
    setLoadingActions((prev) => ({
      ...prev,
      [`${userId}-${action}`]: isLoading,
    }));
  };

  const isActionLoading = (userId: number, action: string) => {
    return loadingActions[`${userId}-${action}`] || false;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/user");
      setUsers(data.content || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || "Erro ao buscar usuários");
    } finally {
      setLoading(false);
    }
  };

  const updateUsersState = async () => {
    try {
      const { data } = await api.get("/user");
      setUsers(data.content || []);
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Erro ao atualizar lista de usuários"
      );
    }
  };

  const handleSendUserToTwygo = async (userId: number) => {
    try {
      setActionLoading(userId, "send", true);

      const user = users.find((u) => u.id === userId);
      const userProfile = profiles.find(
        (profile) => profile.id === user?.profile
      );
      const profileName = userProfile?.name || "";

      await api.post("/twygo/send-user-twygo", {
        user_id: userId,
        role: profileName,
      });

      await updateUsersState();
      message.success("Usuário enviado com sucesso!");
    } catch (error: any) {
      if (
        (error.response?.data?.message ===
          "Este usuário já existe e está inativo na empresa." ||
          error.response?.data?.message === "Aluno já existe") &&
        error.response?.data?.student
      ) {
        const twygoStudentId = error.response.data.student.id;
        const authenticationLink = error.response.data.student.links?.find(
          (link: any) => link.rel === "authenticate_sso"
        );

        message.info(
          `Usuário ${userId} já existe${
            error.response?.data?.message === "Aluno já existe"
              ? ""
              : " e está inativo"
          }. Reativando...`
        );

        if (twygoStudentId && authenticationLink) {
          try {
            const magicLinkUrl = `https://universidade.franqueadosthebest.com${authenticationLink.url}`;

            await api.put(`/user/${userId}`, {
              user_twygo_id: twygoStudentId,
              magic_link_twygo: magicLinkUrl,
            });

            await api.post(`/twygo/${userId}/restore`);
            await updateUsersState();
            message.success("Usuário reativado com sucesso!");
          } catch (restoreError: any) {
            message.error(
              restoreError.response?.data?.message || "Erro ao reativar usuário"
            );
          }
        }
      } else {
        message.error(
          error.response?.data?.message || "Erro ao enviar usuário"
        );
      }
    } finally {
      setActionLoading(userId, "send", false);
    }
  };

  const handleInactivateUser = async (userId: number) => {
    try {
      setActionLoading(userId, "inactivate", true);
      await api.patch(`/twygo/${userId}/inactivate`);
      await updateUsersState();
      message.success("Usuário inativado com sucesso!");
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Erro ao inativar usuário"
      );
    } finally {
      setActionLoading(userId, "inactivate", false);
    }
  };

  const handleRestoreUser = async (userId: number) => {
    try {
      setActionLoading(userId, "restore", true);
      await api.post(`/twygo/${userId}/restore`);
      await updateUsersState();
      message.success("Usuário restaurado com sucesso!");
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Erro ao restaurar usuário"
      );
    } finally {
      setActionLoading(userId, "restore", false);
    }
  };

  const handleFetchUserCourses = async (userId: number) => {
    setIsModalVisible(true);
    setActionLoading(userId, "courses", true);

    try {
      setSelectedUser(
        users.find((user) => user.user_twygo_id === userId) || null
      );
    } finally {
      setActionLoading(userId, "courses", false);
    }
  };

  const handleSelectAll = (type: "send" | "inactivate" | "activate") => {
    setActionType(type);

    switch (type) {
      case "send":
        setStatusFilter("not_registered");
        break;
      case "inactivate":
        setStatusFilter("active");
        break;
      case "activate":
        setStatusFilter("inactive");
        break;
    }

    let selectedIds: number[] = [];

    const filteredByAction = filteredUsers.filter((user) => {
      switch (type) {
        case "send":
          return !user.user_twygo_id;
        case "inactivate":
          return user.user_twygo_id && user.user_twygo_status;
        case "activate":
          return user.user_twygo_id && !user.user_twygo_status;
        default:
          return false;
      }
    });

    selectedIds = filteredByAction
      .map((user) => user.id || 0)
      .filter((id) => id !== 0);

    setSelectedRowKeys(selectedIds);
  };

  const handleDeselectAll = () => {
    setSelectedRowKeys([]);
    setActionType(null);
    setStatusFilter("all");
  };

  const handleBulkAction = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Selecione pelo menos um usuário");
      return;
    }

    try {
      setLoading(true);
      const actionLogs: Array<{
        message: string;
        type: "success" | "error" | "info";
      }> = [];
      let successCount = 0;
      let errorCount = 0;
      const totalUsers = selectedRowKeys.length;
      let modalTitle = "";

      switch (actionType) {
        case "send":
          modalTitle = "Logs de Envio de Usuários para Twygo";
          actionLogs.push({
            message: "Iniciando envio de usuários para Twygo...",
            type: "info",
          });

          for (const [index, userId] of selectedRowKeys.entries()) {
            try {
              const user = users.find((u) => u.id === userId);
              actionLogs.push({
                message: `Enviando usuário: ${user?.full_name || userId}...`,
                type: "info",
              });

              const userProfile = profiles.find(
                (profile) => profile.id === user?.profile
              );
              const profileName = userProfile?.name || "";

              await api.post("/twygo/send-user-twygo", {
                user_id: userId,
                role: profileName,
              });

              successCount++;

              actionLogs.push({
                message: `Usuário ${
                  user?.full_name || userId
                } enviado com sucesso.`,
                type: "success",
              });
            } catch (error: any) {
              const user = users.find((u) => u.id === userId);
              if (
                (error.response?.data?.message ===
                  "Este usuário já existe e está inativo na empresa." ||
                  error.response?.data?.message === "Aluno já existe") &&
                error.response?.data?.student
              ) {
                const twygoStudentId = error.response.data.student.id;
                const authenticationLink =
                  error.response.data.student.links?.find(
                    (link: any) => link.rel === "authenticate_sso"
                  );

                actionLogs.push({
                  message: `Usuário ${user?.full_name || userId} já existe${
                    error.response?.data?.message === "Aluno já existe"
                      ? ""
                      : " e está inativo"
                  }. Reativando...`,
                  type: "info",
                });

                if (twygoStudentId && authenticationLink) {
                  try {
                    const magicLinkUrl = `https://universidade.franqueadosthebest.com${authenticationLink.url}`;

                    await api.put(`/user/${userId}`, {
                      user_twygo_id: twygoStudentId,
                      magic_link_twygo: magicLinkUrl,
                    });

                    await api.post(`/twygo/${userId}/restore`);

                    successCount++;

                    actionLogs.push({
                      message: `Usuário ${
                        user?.full_name || userId
                      } reativado com sucesso.`,
                      type: "success",
                    });
                  } catch (restoreError: any) {
                    errorCount++;
                    const errorMessage =
                      restoreError.response?.data?.message ||
                      "Erro ao reativar usuário";

                    actionLogs.push({
                      message: `Erro ao reativar usuário ${
                        user?.full_name || userId
                      }: ${errorMessage}`,
                      type: "error",
                    });
                  }
                }
              } else {
                errorCount++;
                const failedUser = users.find((u) => u.id === userId);
                const errorMessage =
                  error.response?.data?.message || "Erro desconhecido";

                actionLogs.push({
                  message: `Erro ao enviar usuário ${
                    failedUser?.full_name || userId
                  }: ${errorMessage}`,
                  type: "error",
                });
              }
            }

            const progress = Math.round(((index + 1) / totalUsers) * 100);
            setProgressPercent(progress);
            handleOpenLogsModal(actionLogs, progress, modalTitle);
          }

          actionLogs.push({
            message: `Processo concluído. Enviados com sucesso: ${successCount}. Falhas: ${errorCount}.`,
            type: "info",
          });

          handleOpenLogsModal(actionLogs, 100, modalTitle);
          break;

        case "inactivate":
          modalTitle = "Logs de Inativação de Usuários";
          actionLogs.push({
            message: "Iniciando inativação de usuários na Twygo...",
            type: "info",
          });

          for (const [index, userId] of selectedRowKeys.entries()) {
            try {
              const user = users.find((u) => u.id === userId);
              actionLogs.push({
                message: `Inativando usuário: ${user?.full_name || userId}...`,
                type: "info",
              });

              await api.patch(`/twygo/${userId}/inactivate`);
              successCount++;

              actionLogs.push({
                message: `Usuário ${
                  user?.full_name || userId
                } inativado com sucesso.`,
                type: "success",
              });
            } catch (error: any) {
              errorCount++;
              const failedUser = users.find((u) => u.id === userId);
              const errorMessage =
                error.response?.data?.message || "Erro desconhecido";

              actionLogs.push({
                message: `Erro ao inativar usuário ${
                  failedUser?.full_name || userId
                }: ${errorMessage}`,
                type: "error",
              });
            }

            const progress = Math.round(((index + 1) / totalUsers) * 100);
            setProgressPercent(progress);
            handleOpenLogsModal(actionLogs, progress, modalTitle);
          }

          actionLogs.push({
            message: `Processo concluído. Inativados com sucesso: ${successCount}. Falhas: ${errorCount}.`,
            type: "info",
          });

          handleOpenLogsModal(actionLogs, 100, modalTitle);
          break;

        case "activate":
          modalTitle = "Logs de Ativação de Usuários";
          actionLogs.push({
            message: "Iniciando ativação de usuários na Twygo...",
            type: "info",
          });

          for (const [index, userId] of selectedRowKeys.entries()) {
            try {
              const user = users.find((u) => u.id === userId);
              actionLogs.push({
                message: `Ativando usuário: ${user?.full_name || userId}...`,
                type: "info",
              });

              await api.post(`/twygo/${userId}/restore`);
              successCount++;

              actionLogs.push({
                message: `Usuário ${
                  user?.full_name || userId
                } ativado com sucesso.`,
                type: "success",
              });
            } catch (error: any) {
              errorCount++;
              const failedUser = users.find((u) => u.id === userId);
              const errorMessage =
                error.response?.data?.message || "Erro desconhecido";

              actionLogs.push({
                message: `Erro ao ativar usuário ${
                  failedUser?.full_name || userId
                }: ${errorMessage}`,
                type: "error",
              });
            }

            const progress = Math.round(((index + 1) / totalUsers) * 100);
            setProgressPercent(progress);
            handleOpenLogsModal(actionLogs, progress, modalTitle);
          }

          actionLogs.push({
            message: `Processo concluído. Ativados com sucesso: ${successCount}. Falhas: ${errorCount}.`,
            type: "info",
          });

          handleOpenLogsModal(actionLogs, 100, modalTitle);
          break;
      }

      await updateUsersState();
      handleDeselectAll();

      if (successCount > 0) {
        if (actionType === "send") {
          message.success(`${successCount} usuário(s) enviado(s) com sucesso!`);
        } else if (actionType === "inactivate") {
          message.success(
            `${successCount} usuário(s) inativado(s) com sucesso!`
          );
        } else if (actionType === "activate") {
          message.success(`${successCount} usuário(s) ativado(s) com sucesso!`);
        }
      }

      if (errorCount > 0) {
        message.warning(
          `Não foi possível processar ${errorCount} usuário(s). Verifique os logs para mais detalhes.`
        );
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Erro ao executar ação em massa"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanyFilter(true);
      const { data } = await api.get("/company");
      setCompanies(data.content || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || "Erro ao buscar lojas");
    } finally {
      setLoadingCompanyFilter(false);
    }
  };

  const fetchCompanyUsers = async (companyId: number) => {
    try {
      setLoadingCompanyUsers(true);
      const { data } = await api.get(`/companyUser/${companyId}/company`);
      setCompanyUsers(data.content || []);
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Erro ao buscar usuários da loja"
      );
    } finally {
      setLoadingCompanyUsers(false);
    }
  };

  const handleOpenBulkCoursesModal = async () => {
    setIsBulkCoursesModalVisible(true);
  };

  const handleOpenLogsModal = (
    newLogs: Array<{ message: string; type: "success" | "error" | "info" }>,
    progress: number,
    title?: string
  ) => {
    if (title) {
      setLogsModalTitle(title);
    }
    setLogs(newLogs);
    setProgressPercent(progress);
    setIsLogsModalVisible(true);
    setLoadingBulkCourses(progress < 100);
  };

  const fetchProfiles = async () => {
    try {
      setLoadingProfiles(true);
      const { data } = await api.get("/user?is_profile=true");
      if (data?.content) {
        const profilesData = data.content.map((profile: any) => ({
          id: profile.id,
          name: profile.name,
        }));
        setProfiles(
          profilesData.sort((a: any, b: any) => a.name.localeCompare(b.name))
        );
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || "Erro ao buscar perfis");
    } finally {
      setLoadingProfiles(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const nameMatch =
      filterName === "" ||
      user.full_name?.toLowerCase().includes(filterName.toLowerCase()) ||
      user.email?.toLowerCase().includes(filterName.toLowerCase());

    const companyMatch =
      !selectedCompany ||
      companyUsers.some((companyUser) => companyUser.user_id === user.id);

    let statusMatch = true;
    switch (statusFilter) {
      case "active":
        statusMatch = !!user.user_twygo_id && !!user.user_twygo_status;
        break;
      case "inactive":
        statusMatch = !!user.user_twygo_id && !user.user_twygo_status;
        break;
      case "not_registered":
        statusMatch = !user.user_twygo_id;
        break;
      default:
        statusMatch = true;
    }

    const profileMatch = !profileFilter || user.profile === profileFilter;

    const levelMatch = !levelFilter || user.level === levelFilter;

    return (
      nameMatch && companyMatch && statusMatch && profileMatch && levelMatch
    );
  });

  const getUsersWithoutMagicLink = () => {
    return users.filter(
      (user) =>
        user.user_twygo_id && !user.magic_link_twygo && !!user.user_twygo_status
    );
  };

  const handleUpdateMagicLinks = async () => {
    const usersToUpdate = getUsersWithoutMagicLink();

    if (usersToUpdate.length === 0) {
      message.warning(
        "Não há usuários que precisam de atualização do Magic Link"
      );
      return;
    }

    try {
      setLoadingMagicLink(true);
      const actionLogs: Array<{
        message: string;
        type: "success" | "error" | "info";
      }> = [];
      let successCount = 0;
      let errorCount = 0;
      const totalUsers = usersToUpdate.length;
      const modalTitle = "Logs de Atualização de Magic Links";

      actionLogs.push({
        message: "Iniciando atualização de Magic Links...",
        type: "info",
      });

      handleOpenLogsModal(actionLogs, 0, modalTitle);

      for (const [index, user] of usersToUpdate.entries()) {
        if (!user.id) continue;

        try {
          const userId = user.id;
          actionLogs.push({
            message: `Atualizando Magic Link para usuário: ${
              user.full_name || userId
            }...`,
            type: "info",
          });

          await api.patch(`/twygo/magic-link/${userId}`);
          successCount++;

          actionLogs.push({
            message: `Magic Link atualizado com sucesso para ${
              user.full_name || userId
            }.`,
            type: "success",
          });
        } catch (error: any) {
          errorCount++;
          const errorMessage =
            error.response?.data?.message || "Erro desconhecido";

          actionLogs.push({
            message: `Erro ao atualizar Magic Link para usuário ${
              user.full_name || user.id
            }: ${errorMessage}`,
            type: "error",
          });
        }

        const progress = Math.round(((index + 1) / totalUsers) * 100);
        setProgressPercent(progress);
        handleOpenLogsModal(actionLogs, progress, modalTitle);
      }

      actionLogs.push({
        message: `Processo concluído. Magic Links atualizados com sucesso: ${successCount}. Falhas: ${errorCount}.`,
        type: "info",
      });

      handleOpenLogsModal(actionLogs, 100, modalTitle);

      await updateUsersState();

      if (successCount > 0) {
        message.success(
          `${successCount} Magic Link(s) atualizado(s) com sucesso!`
        );
      }

      if (errorCount > 0) {
        message.warning(
          `Não foi possível atualizar o Magic Link de ${errorCount} usuário(s). Verifique os logs para mais detalhes.`
        );
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Erro ao atualizar Magic Links"
      );
    } finally {
      setLoadingMagicLink(false);
    }
  };

  const handleOpenHelpModal = () => {
    setIsHelpModalVisible(true);
    setShowInitialTooltip(false);

    localStorage.setItem("hasSeenHelpTwygo", "true");
    setShowHelpIconPulse(false);
  };

  const handleInactivateUserAndUpdateSelection = async (userId: number) => {
    await handleInactivateUser(userId);
    handleDeselectAll();
  };

  const handleRestoreUserAndUpdateSelection = async (userId: number) => {
    await handleRestoreUser(userId);
    handleDeselectAll();
  };

  const isAnyUserActionLoading = () => {
    return Object.keys(loadingActions).some(
      (key) => loadingActions[key] === true
    );
  };

  return (
    <PageContainer route="Integração com a Twygo">
      <Container>
        <CardContainer>
          <ContainerSearch>
            <BoxSearch>
              <Input
                placeholder="Buscar por nome ou email"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                prefix={<SearchIcon />}
                disabled={loading}
                allowClear
              />
              <Select
                placeholder="Filtrar por status"
                value={statusFilter}
                onChange={(value) => setStatusFilter(value as string)}
                disabled={loading}
              >
                <Select.Option value="all">Todos</Select.Option>
                <Select.Option value="active">Ativos</Select.Option>
                <Select.Option value="inactive">Inativos</Select.Option>
                <Select.Option value="not_registered">
                  Não Cadastrados
                </Select.Option>
              </Select>
              <Select
                showSearch
                allowClear
                placeholder="Filtrar por loja"
                value={selectedCompany}
                onChange={(value) => setSelectedCompany(value as number)}
                loading={loadingCompanyFilter}
                disabled={loading || loadingCompanyFilter}
                filterOption={(input, option) =>
                  typeof option?.children === "string" &&
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                    0
                }
                defaultActiveFirstOption={false}
                showArrow={false}
              >
                {companies.map((company) => (
                  <Select.Option key={company.id || 0} value={company.id || 0}>
                    {company.company_name}
                  </Select.Option>
                ))}
              </Select>
              <Select
                showSearch
                allowClear
                placeholder="Filtrar por cargo"
                value={profileFilter}
                onChange={(value) => setProfileFilter(value as number)}
                loading={loadingProfiles}
                disabled={loading || loadingProfiles}
                filterOption={(input, option) =>
                  typeof option?.children === "string" &&
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                    0
                }
                defaultActiveFirstOption={false}
                showArrow={false}
              >
                {profiles.map((profile) => (
                  <Select.Option key={profile.id} value={profile.id}>
                    {profile.name}
                  </Select.Option>
                ))}
              </Select>
              <Select
                allowClear
                placeholder="Filtrar por nível"
                value={levelFilter}
                onChange={(value) => setLevelFilter(value as number)}
                disabled={loading}
              >
                {UserLevelDisplay.map((level) => (
                  <Select.Option key={level.value} value={level.value}>
                    {level.label}
                  </Select.Option>
                ))}
              </Select>
              <HelpContainer>
                <div style={{ position: "relative" }}>
                  <Tooltip title="Ajuda">
                    <InfoIcon
                      onClick={handleOpenHelpModal}
                      className={showHelpIconPulse ? "pulse" : ""}
                    />
                  </Tooltip>
                  {showInitialTooltip && (
                    <HelpTooltip>Clique para ver instruções</HelpTooltip>
                  )}
                </div>
              </HelpContainer>
            </BoxSearch>
            <ActionButtonsContainer>
              {(() => {
                const usersWithoutMagicLink = getUsersWithoutMagicLink();
                const hasUsersToUpdate = usersWithoutMagicLink.length > 0;

                return (
                  <Tooltip
                    title={
                      hasUsersToUpdate
                        ? "Atualiza o Magic Link para usuários ativos na Twygo que não têm Magic Link configurado. Apenas usuários com status ATIVO são considerados."
                        : "Não há usuários ativos que necessitam de atualização do Magic Link. Apenas usuários com status ATIVO são considerados."
                    }
                  >
                    <Button
                      actionType="update_magic_link"
                      type="primary"
                      onClick={handleUpdateMagicLinks}
                      loading={loadingMagicLink}
                      disabled={!hasUsersToUpdate || isAnyUserActionLoading()}
                    >
                      Atualizar Magic Links ({usersWithoutMagicLink.length})
                    </Button>
                  </Tooltip>
                );
              })()}
              <Tooltip
                title={
                  selectedRowKeys.length === 0 ||
                  !selectedRowKeys.every((id) => {
                    const user = users.find((u) => u.id === id);
                    return user?.user_twygo_id && user?.user_twygo_status;
                  })
                    ? "Selecione os usuários 'Ativos' na Twygo para vincular cursos em massa."
                    : isAnyUserActionLoading()
                    ? "Por favor, aguarde a conclusão da operação em andamento."
                    : ""
                }
              >
                <Button
                  actionType="activate_courses_mass"
                  type="primary"
                  onClick={handleOpenBulkCoursesModal}
                  disabled={
                    selectedRowKeys.length === 0 ||
                    !selectedRowKeys.every((id) => {
                      const user = users.find((u) => u.id === id);
                      return user?.user_twygo_id && user?.user_twygo_status;
                    }) ||
                    isAnyUserActionLoading()
                  }
                >
                  Vincular Cursos em Massa
                </Button>
              </Tooltip>
            </ActionButtonsContainer>
          </ContainerSearch>
          <ContainerActions>
            <BoxActions>
              {(statusFilter === "not_registered" ||
                statusFilter === "all") && (
                <Checkbox
                  checked={actionType === "send"}
                  onChange={(e) =>
                    e.target.checked
                      ? handleSelectAll("send")
                      : handleDeselectAll()
                  }
                  disabled={loading || isAnyUserActionLoading()}
                >
                  <LabelCheckbox>
                    Selecionar usuários não cadastrados na Twygo
                  </LabelCheckbox>
                </Checkbox>
              )}
              {(statusFilter === "active" || statusFilter === "all") && (
                <Checkbox
                  checked={actionType === "inactivate"}
                  onChange={(e) =>
                    e.target.checked
                      ? handleSelectAll("inactivate")
                      : handleDeselectAll()
                  }
                  disabled={loading || isAnyUserActionLoading()}
                >
                  <LabelCheckbox>
                    Selecionar usuários ativos na Twygo
                  </LabelCheckbox>
                </Checkbox>
              )}
              {(statusFilter === "inactive" || statusFilter === "all") && (
                <Checkbox
                  checked={actionType === "activate"}
                  onChange={(e) =>
                    e.target.checked
                      ? handleSelectAll("activate")
                      : handleDeselectAll()
                  }
                  disabled={loading || isAnyUserActionLoading()}
                >
                  <LabelCheckbox>
                    Selecionar usuários inativos na Twygo
                  </LabelCheckbox>
                </Checkbox>
              )}
            </BoxActions>
            {selectedRowKeys.length > 0 && (
              <Actions>
                <UsersSelected>
                  {selectedRowKeys.length} usuário(s) selecionado(s)
                </UsersSelected>
                <Tooltip
                  title={
                    isAnyUserActionLoading()
                      ? "Por favor, aguarde a conclusão da operação em andamento."
                      : actionType === "send"
                      ? "Ao clicar em 'Enviar para Twygo', os usuários selecionados serão enviados para a Twygo."
                      : actionType === "inactivate"
                      ? "Ao clicar em 'Inativar Usuários', os usuários selecionados serão inativados na Twygo."
                      : "Ao clicar em 'Ativar Usuários', os usuários selecionados serão ativados na Twygo."
                  }
                >
                  <Button
                    actionType={actionType || "activate"}
                    type="primary"
                    onClick={handleBulkAction}
                    loading={loading}
                    disabled={isAnyUserActionLoading()}
                  >
                    {actionType === "send" && "Enviar para Twygo"}
                    {actionType === "inactivate" && "Inativar Usuários"}
                    {actionType === "activate" && "Ativar Usuários"}
                  </Button>
                </Tooltip>
              </Actions>
            )}
          </ContainerActions>
        </CardContainer>

        <TwygoUsers
          users={users}
          loading={loading}
          loadingCompanyUsers={loadingCompanyUsers}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          setActionType={setActionType}
          companyUsers={companyUsers}
          selectedCompany={selectedCompany}
          filteredUsers={filteredUsers}
          isActionLoading={isActionLoading}
          handleSendUserToTwygo={handleSendUserToTwygo}
          handleInactivateUser={handleInactivateUserAndUpdateSelection}
          handleRestoreUser={handleRestoreUserAndUpdateSelection}
          handleFetchUserCourses={handleFetchUserCourses}
          profiles={profiles}
        />

        <TwygoCourses
          isVisible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
            setSelectedUser(null);
          }}
          selectedUser={selectedUser}
          onCoursesUpdated={updateUsersState}
        />

        <TwygoMassiveCourses
          isVisible={isBulkCoursesModalVisible}
          onClose={() => {
            setIsBulkCoursesModalVisible(false);
          }}
          selectedUsers={selectedRowKeys as number[]}
          users={users}
          onOpenLogsModal={handleOpenLogsModal}
        />

        <TwygoLogsModal
          isVisible={isLogsModalVisible}
          onClose={() => {
            setIsLogsModalVisible(false);
            if (loadingBulkCourses) {
              setIsBulkCoursesModalVisible(false);
            }
          }}
          loadingBulkCourses={loadingBulkCourses}
          progressPercent={progressPercent}
          logs={logs}
          title={logsModalTitle}
        />

        <TwygoInfoModal
          isVisible={isHelpModalVisible}
          onClose={() => setIsHelpModalVisible(false)}
        />
      </Container>
    </PageContainer>
  );
};

export default Twygo;
